import { z } from 'zod';

/**
 * Documento do editor (TipTap/ProseMirror) guardado como JSON.
 * Só nós e marcas desta lista são aceitos: o conteúdo nunca vira HTML cru,
 * então não há como injetar script. Links só com http(s) ou mailto.
 */
const NODE_TYPES = [
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'hardBreak',
  'horizontalRule',
] as const;
const MARK_TYPES = ['bold', 'italic', 'strike', 'code', 'link', 'underline'] as const;

const safeHref = z
  .string()
  .max(2000)
  .refine((v) => /^(https?:|mailto:)/i.test(v), 'Link inválido');

const mark = z.object({
  type: z.enum(MARK_TYPES),
  attrs: z
    .object({
      href: safeHref.optional(),
      target: z.string().max(20).nullable().optional(),
      rel: z.string().max(100).nullable().optional(),
      class: z.null().optional(),
    })
    .optional(),
});

export type RichNode = {
  type: (typeof NODE_TYPES)[number];
  attrs?: Record<string, string | number | boolean | null>;
  content?: RichNode[];
  text?: string;
  marks?: z.infer<typeof mark>[];
};

export const richNode: z.ZodType<RichNode> = z.lazy(() =>
  z.object({
    type: z.enum(NODE_TYPES),
    attrs: z.record(z.string(), z.union([z.string().max(200), z.number(), z.boolean(), z.null()])).optional(),
    content: z.array(richNode).max(2000).optional(),
    text: z.string().max(20_000).optional(),
    marks: z.array(mark).max(10).optional(),
  }),
);

export const richTextSchema = richNode
  .refine((n) => n.type === 'doc', 'Documento inválido')
  .refine((n) => JSON.stringify(n).length <= 200_000, 'Texto longo demais');

export type RichText = RichNode;

/** Texto puro do documento (busca, resumos, notificações). */
export function richTextToPlain(node: RichNode | null | undefined): string {
  if (!node) return '';
  if (node.text) return node.text;
  const sep = ['paragraph', 'heading', 'listItem', 'blockquote', 'codeBlock'].includes(node.type) ? '\n' : '';
  return (node.content ?? []).map(richTextToPlain).join('') + sep;
}
